import { Controller } from '@hotwired/stimulus';

// Keep one Turbo Stream destination for notices, but render it inside the active
// native dialog: a page-level z-index cannot rise above a dialog's backdrop.
export default class extends Controller {
  connect() {
    this.closing = event => this.place(event.target.matches?.('dialog') ? event.target : event.target.querySelector?.('dialog'));
    this.beforeCache = () => this.moveTo(this.home);
    document.addEventListener('modal:closing', this.closing, true);
    // UTMR removes dialogs before caching. Keep the destination in the snapshot
    // so Back/Forward and subsequent component responses can still show notices.
    document.addEventListener('turbo:before-cache', this.beforeCache, true);
    this.observer = new MutationObserver(records => {
      if (records.some(record => record.target instanceof HTMLDialogElement ||
        [...record.addedNodes, ...record.removedNodes].some(node =>
          node instanceof Element && (node.matches('dialog') || node.querySelector('dialog'))))) this.place();
    });
    this.observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'data-closing'] });
    this.place();
  }

  // Move out before a dialog is removed, so notices survive closing a drawer.
  // Include confirm dialogs; their backdrop also makes the underlying UI inert.
  place(closingDialog = null) {
    const dialogs = [...document.querySelectorAll('dialog.utmr[open]:not([data-closing])')]
      .filter(dialog => !closingDialog?.contains(dialog));
    const dialog = dialogs.at(-1);
    const parent = dialog?.querySelector('[data-modal-target~="content"]') || dialog || this.home;
    this.moveTo(parent);
  }

  prepare(event) {
    // Preserve the stream destination when Turbo replaces its current ancestor.
    // Its page position must stay stable: moving it to the end of body makes
    // Turbo relocate the page shell and reconnect all its Stimulus controllers.
    if (event.target !== this.element && event.target.contains?.(this.element)) this.moveTo(this.home);
    const incoming = event.detail.newBody || event.detail.newFrame;
    const alerts = incoming?.querySelector('#alerts');
    if (!alerts) return;

    this.element.toggleAttribute('data-turbo-permanent', !alerts.querySelector('[data-bootstrap--alert-target="toast"]'));
  }

  get home() { return document.getElementById('flash_messages'); }

  moveTo(parent) {
    if (parent && this.element.parentElement !== parent) parent.append(this.element);
  }

  disconnect() {
    this.observer.disconnect();
    document.removeEventListener('modal:closing', this.closing, true);
    document.removeEventListener('turbo:before-cache', this.beforeCache, true);
  }
}
